import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { Spinner } from './Spinner'

/** Visual variant for Button: `'primary'` | `'secondary'` | `'danger'` | `'ghost'`. */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
/** Size preset for Button: `'sm'` | `'md'` | `'lg'`. */
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-primary-500 border border-transparent',
  secondary:
    'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 focus-visible:ring-primary-500',
  danger:
    'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 border border-transparent',
  ghost:
    'bg-transparent text-gray-600 hover:bg-gray-100 border border-transparent focus-visible:ring-primary-500',
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs rounded-md',
  md: 'h-9 px-4 text-sm rounded-md',
  lg: 'h-11 px-6 text-base rounded-lg',
}

/** Generic button with variant, size, loading spinner, and `forwardRef` support. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, disabled, children, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={twMerge(
          clsx(
            'btn',
            variantClasses[variant],
            sizeClasses[size],
            (disabled || loading) && 'opacity-60 pointer-events-none',
            className
          )
        )}
        {...props}
      >
        {loading && <Spinner size="sm" className="mr-1" />}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
